import type { MenuItemWithDetails } from '../../lib/twin';
import { formatPrice } from '../../lib/twin';

interface Props {
  item: MenuItemWithDetails;
}

/**
 * A single featured menu card. All content is grounded: name, description, and
 * price come straight from the twin's `menu_item` record. Price is formatted from
 * `price_cents` — never hand-typed.
 */
export function FeaturedItemCard({ item }: Props) {
  const hasOptions = item.options.length > 0;

  return (
    <li className="flex h-full flex-col overflow-hidden rounded-[var(--radius-card)] bg-cream-50 ring-1 ring-mocha/15">
      {/* Image placeholder — decorative. */}
      <div
        aria-hidden="true"
        className="flex aspect-[3/2] items-center justify-center bg-gradient-to-tr from-foam to-cream-200"
      >
        <span className="text-5xl">☕</span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold">{item.name}</h3>
          <span className="shrink-0 rounded-full bg-accent-soft px-3 py-1 text-sm font-bold text-accent-dark">
            {formatPrice(item.price_cents)}
          </span>
        </div>

        {item.category && (
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-latte">
            {item.category.name}
          </p>
        )}

        <p className="mt-2 flex-1 text-espresso-soft">{item.description}</p>

        {hasOptions && (
          <p className="mt-3 text-sm text-mocha">
            Customizable: {[...new Set(item.options.map((o) => o.group))].join(' · ')}
          </p>
        )}

        <a
          href="#menu"
          className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-full bg-espresso px-5 py-2.5 font-semibold text-cream-50 transition-colors hover:bg-espresso-soft"
          aria-label={`Order ${item.name}`}
        >
          Order {item.name}
        </a>
      </div>
    </li>
  );
}
